import { OptimizelyDecision } from '@optimizely/react-sdk';
import type { RecurlyError } from '../../../interfaces';
import { SEASONAL_PLAN_CODE } from '../../utils/constants';
import {
  BuildAddonsCodeProps,
  CreatePricingObjectProps,
  PricingObject,
} from './GtbOrderSummaryTypes';

const createPricingObject = ({
  planCode,
  tax,
  addonCode,
  promoCode,
}: CreatePricingObjectProps): PricingObject => {
  // check if code is a gift card
  if (promoCode?.length === 16) {
    return {
      subscriptions: [
        {
          plan: planCode,
          addons: [{ code: addonCode, quantity: 1 }],
        },
      ],
      tax: {
        amount: { now: tax, next: 0 },
      },
      giftCard: promoCode,
    };
  }

  return {
    subscriptions: [
      {
        plan: planCode,
        addons: [{ code: addonCode, quantity: 1 }],
      },
    ],
    tax: {
      amount: { now: tax, next: 0 },
    },
    coupon: promoCode,
  };
};

const createEmptyRecurlyError = (): Partial<RecurlyError> => ({
  code: '',
  message: '',
  name: '',
});

const buildAddonsCode = ({ addons, shipState, shipCountry }: BuildAddonsCodeProps): string => {
  const addonsCode = `${shipCountry}-${shipState}`;
  const addonCodes = addons?.map((addon) => addon.code);
  if (addonCodes?.includes(addonsCode)) {
    return addonsCode;
  }
  return `${shipCountry}-ALL`;
};

const getSubscriptionName = (planCode: string): string => {
  if (planCode === SEASONAL_PLAN_CODE) return 'Seasonal Subscription';
  return 'Annual Subscription';
};

const isBrowser = (): boolean => ![typeof window, typeof document].includes('undefined');

const getParamFromUrl = (param: string): string => {
  if (!isBrowser()) return '';

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const paramValue = urlParams.get(param);
  return paramValue || '';
};

const isCallSuccessful = (status: number): boolean => status >= 200 && status < 300;

const isSeasonalPlan = (planCode: string) => planCode === SEASONAL_PLAN_CODE;

const calculateAdjustedPrice = (
  price: number,
  planCode: string,
  flag: OptimizelyDecision | undefined,
): number => {
  if (isSeasonalPlan(planCode) && flag?.enabled) {
    const basePlanPrice = price || 0;
    const increasedAmount = flag.variables?.increasedPrice as number;
    const total = basePlanPrice + increasedAmount;
    return parseFloat(total.toFixed(2));
  }
  return price;
};

export {
  buildAddonsCode,
  createEmptyRecurlyError,
  createPricingObject,
  getParamFromUrl,
  getSubscriptionName,
  isBrowser,
  isCallSuccessful,
  calculateAdjustedPrice,
};
